import React, { useState, useEffect, useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk'
import { Check, ChevronsUpDown, Loader2, CircleX } from 'lucide-react'
import { facturamaService, FacturamaCatalogItem } from '@/app/services/facturama.service'
import { useDebounce } from '../../../hooks/useDebounce'

export interface SatKeySelectProps {
  value?: string
  selectedName?: string
  onChange: (code: string, name: string) => void
  onClear: () => void
  placeholder?: string
  className?: string
  isLoading?: boolean
}

export function SatKeySelect({
  value,
  selectedName,
  onChange,
  onClear,
  placeholder = 'Buscar clave SAT...',
  className = '',
  isLoading = false,
}: SatKeySelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const [items, setItems] = useState<FacturamaCatalogItem[]>([])
  const [loading, setLoading] = useState(false)

  const fetchKeys = useCallback(async (keyword: string) => {
    if (!keyword || keyword.length < 3) {
      setItems([])
      return
    }
    setLoading(true)
    try {
      const results = await facturamaService.searchCatalogs('ProductsOrServices', keyword)
      setItems(results || [])
    } catch (error) {
      console.error('Error fetching SAT keys:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      fetchKeys(debouncedSearch)
    }
  }, [debouncedSearch, open, fetchKeys])

  const handleSelect = (item: FacturamaCatalogItem) => {
    onChange(item.Value, item.Name)
    setOpen(false)
    setSearch('')
  }

  const displayValue = value ? `${value} - ${selectedName || ''}` : ''

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
          onClick={() => setOpen(!open)}
        >
          <span className="truncate">
            {displayValue || <span className="text-muted-foreground">{placeholder}</span>}
          </span>
          <div className="flex items-center gap-2">
            {isLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {!isLoading && value && (
              <CircleX
                className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  onClear()
                }}
              />
            )}
            {!isLoading && (
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            )}
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[400px] p-0 z-50 bg-popover text-popover-foreground rounded-md border shadow-md" align="start">
        <Command shouldFilter={false} className="border-none rounded-md overflow-hidden">
          <CommandInput
            placeholder="Escriba para buscar en el SAT..."
            value={search}
            onValueChange={setSearch}
            className="border-none focus:ring-0 px-3 py-2 text-sm w-full outline-none"
          />
          <CommandList className="max-h-[300px] overflow-y-auto p-1">
            {loading && (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && items.length === 0 && search.length >= 3 && (
              <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
                No se encontraron resultados
              </CommandEmpty>
            )}
            {!loading && search.length < 3 && items.length === 0 && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Escriba al menos 3 caracteres...
              </div>
            )}

            {!loading && items.length > 0 && (
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.Value}
                    value={item.Value}
                    onSelect={() => handleSelect(item)}
                    className="flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground aria-selected:bg-accent aria-selected:text-accent-foreground"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{item.Value}</span>
                      <span className="text-xs text-muted-foreground">{item.Name}</span>
                    </div>
                    {value === item.Value && <Check className="ml-2 h-4 w-4" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
